/**
 * UserMenu — account-switcher dropdown anchored in the AppBar.
 *
 * Renders nothing when currentUser is null (unauthenticated). When present,
 * shows an Avatar IconButton that opens a MUI Menu with a header giving name,
 * email and role, then preferences, about and sign out.
 *
 * It used to carry a role switcher as well, fetched on first open. That has
 * gone with the model that needed it — an account is one role now, so the list
 * held a single permanently-disabled entry under a heading offering a choice
 * that did not exist.
 */
import { useState } from 'react';
import AboutDialog from './AboutDialog';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../core/auth';
import { normalizeUserType } from '../core/auth/userTypes';
import { nameTokens } from './nameTokens';
import { useNavigationGuard } from './navigationGuardContext';

// ── Stable element IDs for aria linkage ───────────────────────────────────
const TRIGGER_ID = 'account-menu-button';
const MENU_ID = 'account-menu';

// ── Component ─────────────────────────────────────────────────────────────

export default function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { attemptNavigate } = useNavigationGuard();
  const { currentUser, logout } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  // About dialog visibility.
  const [aboutOpen, setAboutOpen] = useState(false);

  // Nothing in this menu is async any more; kept as a single flag so the items
  // below did not all have to change shape.
  const allDisabled = false;

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Close the menu, then navigate on the following task.
   *
   * Navigating in the same tick as the close left the menu open and parked in
   * the bottom-left corner of the window. Every page here is `lazy()`, so
   * `navigate` suspends the tree while the chunk loads — and React cannot
   * commit the `setAnchorEl(null)` from this same tick while it is suspended.
   * The Menu renders into a portal on `document.body`, so it stays visible
   * through the suspension, but re-renders with `anchorEl` already null and
   * falls back to MUI's origin position.
   *
   * Deferring lets the close commit and the portal unmount first, so the
   * suspension has nothing left to strand.
   */
  const closeThenNavigate = (to: string, options?: { replace?: boolean }) => {
    handleClose();
    setTimeout(() => attemptNavigate(() => navigate(to, options)), 0);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const handleOpenAbout = () => {
    handleClose();
    setAboutOpen(true);
  };

  // ── Render guard ──────────────────────────────────────────────────────
  if (!currentUser) return null;

  // Normalize once here; used for i18n Chip label and isCurrent comparison below.
  const currentRoleEnum = normalizeUserType(currentUser.userType);

  // nameTokens drops the "[SEED]" tag seeded accounts carry — taking name[0]
  // directly rendered a literal "[" in this avatar.
  const firstInitial = (
    nameTokens(currentUser.name)[0]?.[0] ?? currentUser.email?.[0] ?? '?'
  ).toUpperCase();

  return (
    <>
      {/* ── Trigger button ── */}
      <Tooltip title={t('nav.account.openMenu')}>
        <IconButton
          id={TRIGGER_ID}
          color="inherit"
          onClick={handleOpen}
          aria-label={t('nav.account.openMenu')}
          aria-haspopup="menu"
          aria-expanded={isOpen ? 'true' : undefined}
          aria-controls={isOpen ? MENU_ID : undefined}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'secondary.main',
              fontSize: '0.875rem',
            }}
          >
            {firstInitial}
          </Avatar>
        </IconButton>
      </Tooltip>

      {/* ── Dropdown menu ── */}
      <Menu
        id={MENU_ID}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': TRIGGER_ID }}
        sx={{ '& .MuiPaper-root': { minWidth: 280 } }}
        // MUI default: unmounts content while closed — satisfies the
        // "Menu must NOT keep mounted while closed" acceptance criterion.
        keepMounted={false}
      >

        {/* ── Header — non-interactive identity block ── */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {currentUser.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {currentUser.email}
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            <Chip
              label={t(`USER-TYPES-ENUM.${currentRoleEnum}`)}
              size="small"
              variant="outlined"
              color="secondary"
            />
          </Box>
        </Box>

        <Divider />

        {/* Role switching used to sit here. It is gone with the model that
            needed it: an account is one role now, so the list had exactly one
            entry, permanently disabled, under a heading offering a choice that
            did not exist. */}

        {/* ── Preferences ── */}
        <MenuItem
          onClick={() => closeThenNavigate('/preferences')}
          disabled={allDisabled}
          aria-keyshortcuts="Meta+, Control+,"
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('PREFERENCES.title')}</ListItemText>
        </MenuItem>

        {/* ── About ── */}
        <MenuItem onClick={handleOpenAbout} disabled={allDisabled}>
          <ListItemIcon>
            <InfoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('nav.about')}</ListItemText>
        </MenuItem>

        {/* ── Sign out ── */}
        <MenuItem onClick={handleLogout} disabled={allDisabled}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('nav.logout')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* ── About dialog — rendered outside the Menu so it survives Menu unmount ── */}
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  );
}
